import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import DropDownForm from "../DropDownForm";
import FormInputField from "../FormInputField";
import MakeGetRequest from "../utils/MakeGetRequest";
import AddSubGenres from "./AddSubGenres";
import makePutRequest from "../utils/makePutRequest";
import makePostRequest from "../utils/makePostRequest";
import makeDeleteRequest from "../utils/makeDeleteRequest";
import { isMailValid, isUrlValid } from "../utils/validators";
import PopUpModulBtn from "../PopUpModulBtn";
import RichTextEditorField from "../RichTextEditorField";

const getPlainTextFromHtml = (value = "") =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const CreateBandForm = () => {
  // Get params
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const slug = params.get("slug");
  // Fetch Band data
  const { data: bandData } = MakeGetRequest({
    route: `get_band_edit?slug=${slug}`,
    errorCodeRedirect: 401,
    forceToken: true,
  });
  const profilePageUrl = `/music/${slug}`;

  const [postLoading, setPostLoading] = useState(false);
  const [postError, setPostError] = useState("");
  const [postImageError, setPostImageError] = useState("");
  const [altGenres, setAltGenres] = useState([]);
  const [deleteProjectInput, setDeleteProjectInput] = useState("");
  const [deleteButtonIsActive, setDeleteButtonIsActive] = useState(false);
  const [bandDescription, setBandDescription] = useState("");

  // Submit Basic Info Form
  const formBasics = useRef(null);
  const formImages = useRef(null);
  const formSubGenres = useRef(null);
  const formSocials = useRef(null);
  const formStreaming = useRef(null);
  const fromDescription = useRef(null);
  const activeRef = useRef();
  const gigSeekersRef = useRef();
  const spotifyPlaylistRef = useRef(null);

  useEffect(() => {
    setBandDescription(bandData?.band?.description || "");
  }, [bandData?.band?.description]);

  function get_playlistsong_from_prio(prio) {
    for (const song of bandData.playlistsongs) {
      if (song.prio === prio) {
        return song.title
          ? `Nuvarande: ${song.title}`
          : `Nuvarande: ${song.song_id}`;
      }
    }
    return null;
  }

  // Delete musicproject
  const handleDeleteInputChange = (e) => {
    const value = e.target.value;
    setDeleteProjectInput(value);

    if (value === "radera musikprojekt") {
      setDeleteButtonIsActive(true);
    } else {
      setDeleteButtonIsActive(false);
    }
  };

  const handleDeleteProject = (e) => {
    e.preventDefault();
    if (deleteButtonIsActive) {
      makeDeleteRequest({
        route: `delete_band?slug=${slug}`,
        forceToken: true,
        redirectSuccessPath: "/userprofile",
      });
    }
  };

  const handleSubmitBasic = async (e) => {
    e.preventDefault();
    setPostLoading(true);
    if (formBasics.current.checkValidity()) {
      // Proceed with form submission
      const formElements = formBasics.current.elements;
      const submittedData = {};
      for (let i = 0; i < formElements.length; i++) {
        const element = formElements[i];
        // Dont submit the band name since it cant be changed
        if (element.name == "name") {
          continue;
        }
        if (element.name === "official_homepage" && element.value) {
          if (!isUrlValid(element.value)) {
            setPostError("Ogiltig hemsida. Använd formatet https://dinsida.se");
            setPostLoading(false);
            return;
          }
        }
        if (element.name === "official_mail" && element.value) {
          if (!isMailValid(element.value)) {
            setPostError("Ogiltig mailadress");
            setPostLoading(false);
            return;
          }
        }
        if (element.name) {
          // Ensure the element has a name attribute

          if (element.type === "checkbox") {
            // Make sure it sends data as a bool
            submittedData[element.name] = element.checked;
          } else {
            submittedData[element.name] = element.value;
          }
        }
      }
      try {
        const data = { form: "basic", data: submittedData };
        const { error } = await makePutRequest(
          `update_band?slug=${slug}`,
          data,
        );
        if (error) {
          throw new Error(error);
        }
        // Refresh the page
        window.location.reload();
      } catch (error) {
        console.error("Error updating band:", error);
        setPostError(error.message);
      } finally {
        setPostLoading(false);
      }
    } else {
      // form error
      formBasics.current.reportValidity();
      setPostLoading(false);
    }
  };

  const handleSubmitImages = async (e) => {
    e.preventDefault();
    setPostLoading(true);
    if (formImages.current.checkValidity()) {
      const formElements = formImages.current.elements;
      const fileInputs = [];
      for (let i = 0; i < formElements.length; i++) {
        const element = formElements[i];
        if (element.type === "file" && element.files.length > 0) {
          const fileSizeInMB = element.files[0].size / (1024 * 1024);
          if (fileSizeInMB > 4) {
            setPostImageError("Bilden filstorlek är för stor");
            setPostLoading(false);
            return;
          }
          fileInputs.push({ name: element.name, file: element.files[0] });
        }
      }
      try {
        for (const { name, file } of fileInputs) {
          const formData = new FormData();
          formData.append(name, file);
          const { error } = await makePostRequest(
            `upload_band_image?slug=${slug}`,
            formData,
          );
          if (error) {
            const imageLabel =
              name === "image_profile" ? "profilbild" : "splashbild";
            throw new Error(`${imageLabel}: ${error}`);
          }
        }
        // Refresh the page
        window.location.reload();
      } catch (error) {
        console.error("Error updating band:", error);
        setPostImageError(error.message);
      } finally {
        setPostLoading(false);
      }
    } else {
      // form error
      formImages.current.reportValidity();
      setPostImageError("");
      setPostLoading(false);
    }
  };

  const handleVisibilityProfileToggle = async (e) => {
    e.preventDefault();
    setPostLoading(true);
    const toggleValue = bandData.band.visible ? false : true;
    const submittedData = { visible: toggleValue };
    try {
      const data = { form: "visible", data: submittedData };
      const { error } = await makePutRequest(`update_band?slug=${slug}`, data);
      if (error) {
        throw new Error(error);
      }
      // Refresh the page after submittion
      window.location.reload();
    } catch (error) {
      console.error("Error updating band:", error);
      setPostError(error.message);
    } finally {
      setPostLoading(false);
    }
  };

  const handleSubmitDescription = async (e) => {
    e.preventDefault();
    setPostLoading(true);
    const description = bandDescription.trim();
    const descriptionPlainText = getPlainTextFromHtml(description);

    if (!descriptionPlainText) {
      setPostError("Beskrivningen kan inte vara tom");
      setPostLoading(false);
      return;
    }

    if (descriptionPlainText.length > 4000) {
      setPostError(`För många tecken: ${descriptionPlainText.length}/4000st`);
      setPostLoading(false);
      return;
    }

    const submittedData = { description };
    try {
      const data = { form: "description", data: submittedData };
      const { error } = await makePutRequest(`update_band?slug=${slug}`, data);
      if (error) {
        throw new Error(error);
      }
      // Refresh the page after submittion
      window.location.reload();
    } catch (error) {
      console.error("Error updating band:", error);
      setPostError(error.message);
    } finally {
      setPostLoading(false);
    }
  };

  const handleSubmitSocials = async (e) => {
    e.preventDefault();
    setPostLoading(true);
    if (formSocials.current.checkValidity()) {
      // Proceed with form submission
      const formElements = formSocials.current.elements;
      const submittedData = {};
      for (let i = 0; i < formElements.length; i++) {
        const element = formElements[i];
        const newValue = element.value.trim(); // Normalize
        // Make sure that only changed fields get submitted
        const platformName = element.name;
        const currentValue = bandData.socials[platformName] || "";
        if (newValue !== currentValue) {
          submittedData[element.name] = element.value;
        }
      }
      try {
        const data = { form: "socials", data: submittedData };
        const { error } = await makePutRequest(
          `update_band?slug=${slug}`,
          data,
        );
        if (error) {
          throw new Error(error);
        }
        // Refresh the page after submittion
        window.location.reload();
      } catch (error) {
        setPostError(error.message);
      } finally {
        setPostLoading(false);
      }
    } else {
      // form error
      formBasics.current.reportValidity();
      setPostLoading(false);
    }
  };

  const handleSubmitStreaming = async (e) => {
    e.preventDefault();
    setPostLoading(true);
    if (formStreaming.current.checkValidity()) {
      // Proceed with form submission
      const formElements = formStreaming.current.elements;
      const submittedData = {};
      for (let i = 0; i < formElements.length; i++) {
        const element = formElements[i];
        const newValue = element.value.trim(); // Normalize
        // Make sure that only changed fields get submitted
        const platformName = element.name;
        const currentValue = bandData.streamingplatforms[platformName] || "";
        if (newValue !== currentValue) {
          submittedData[element.name] = element.value;
        }
      }
      try {
        const data = { form: "streaming", data: submittedData };
        const { error } = await makePutRequest(
          `update_band?slug=${slug}`,
          data,
        );
        if (error) {
          throw new Error(error);
        }
        // Refresh the page after submittion
        window.location.reload();
      } catch (error) {
        console.error("Error updating band:", error);
        setPostError(error.message);
      } finally {
        setPostLoading(false);
      }
    } else {
      // form error
      formBasics.current.reportValidity();
      setPostLoading(false);
    }
  };

  const handleSubmitSubGenres = async (e) => {
    e.preventDefault();
    setPostLoading(true);
    if (formSubGenres.current.checkValidity()) {
      const genres = [];
      for (const genre of altGenres) {
        genres.push({ genre: genre.value.trim() });
      }
      const submittedGenres = { genres: genres };
      try {
        const data = { form: "subgenres", data: submittedGenres };
        const { error } = await makePutRequest(
          `update_band?slug=${slug}`,
          data,
        );
        if (error) {
          throw new Error(error);
        }
        // Refresh the page after submittion
        window.location.reload();
      } catch (error) {
        console.error("Error updating band:", error);
        setPostImageError(error.message);
      } finally {
        setPostLoading(false);
      }
    } else {
      // form error
      formImages.current.reportValidity();
      setPostImageError("");
      setPostLoading(false);
    }
  };

  const handleSubmitSpotifyPlaylist = async (e) => {
    e.preventDefault();
    setPostLoading(true);
    if (spotifyPlaylistRef.current.checkValidity()) {
      const urlValidation = "https://open.spotify.com/track/";
      const songs = [];
      const formElements = spotifyPlaylistRef.current.elements;
      for (let i = 0; i < formElements.length; i++) {
        const element = formElements[i];
        if (
          element.tagName === "INPUT" &&
          element.type === "text" &&
          element.value.trim()
        ) {
          const newValue = element.value.trim(); // Normalize
          // Get Prio from field
          let prio = element.name;
          const match = prio.match(/song(\d+)/);
          if (match) {
            const num = match[1]; // Extract the number part
            prio = num;
          }

          if (!newValue.startsWith(urlValidation)) {
            setPostError("Felaktig låtlänk");
            setPostLoading(false);
            return;
          }
          // Check if the song already exists in the list
          if (songs.includes(newValue)) {
            setPostError("Du kan inte ha samma låt två gånger");
            setPostLoading(false);
            return;
          }

          songs.push({ value: newValue, prio: prio });
        }
      }
      try {
        const data = { form: "spotifyplaylist", data: { urls: songs } };
        const { error } = await makePutRequest(
          `update_band?slug=${slug}`,
          data,
        );
        if (error) {
          throw new Error(error);
        }
        // Refresh the page after submittion
        window.location.reload();
      } catch (error) {
        console.error("Error updating band:", error);
      } finally {
        setPostLoading(false);
        setPostError(false);
      }
    } else {
      setPostLoading(false);
    }
  };

  if (bandData) {
    return (
      <>
        <div className="mb-4 ">
          {/* Profile Visibilty controller. */}
          <div className="form-control justify-evenly my-4 w-full px-4">
            <label className="cursor-pointer label">
              <span className="label-text text-base-content font-semibold text-xl">
                Offentlig profil?
              </span>
              <input
                type="checkbox"
                onClick={handleVisibilityProfileToggle}
                defaultChecked={bandData.band.visible}
                className="toggle toggle-success"
              />
            </label>
          </div>
          {/* Basic info form. */}
          <DropDownForm
            title="Grundläggande information"
            about="Ändra huvudinställningarna för ditt projekt"
          >
            <form
              ref={formBasics}
              onSubmit={handleSubmitBasic}
              className="my-4"
            >
              <FormInputField
                type="text"
                name="name"
                disabled={true}
                label="Namn"
                bottomLabel="Går inte att ändra"
                placeholder={bandData.band.name}
                required={true}
              />
              {/* Location temporarily disabled
              <FormAutoComplete
                name="location"
                label="Stad"
                placeholder={bandData.band.location}
                required={true}
                items={bandData.cities}
              />
              */}
              <FormInputField
                type="select"
                name="constellation_type"
                label="Konstellations typ"
                items={bandData.constellations}
                defaultValue={bandData.band.constellation_type}
                itemName="type"
                required={true}
              />
              <FormInputField
                type="select"
                name="genre_main"
                label="Huvudgenre"
                items={bandData.genres}
                defaultValue={bandData.band.genre_main}
                itemName="genre"
                required={true}
              />
              <FormInputField
                type="text"
                name="official_homepage"
                label="Hemsida"
                bottomLabel="Länk till hemsida. Kan lämnas tom"
                defaultValue={bandData.band.official_homepage}
                placeholder="t.ex https://er-sida.se"
              />
              <FormInputField
                type="text"
                name="official_mail"
                label="E-post"
                bottomLabel="Synlig för andra medlemmar. Kan lämnas tom"
                defaultValue={bandData.band.official_mail}
                placeholder="E-postadress"
              />
              <FormInputField
                type="checkbox"
                name="active"
                ref={activeRef}
                label="Är ni aktiva nu?"
                checked={bandData.band.active}
              />
              <FormInputField
                type="checkbox"
                name="gig_seekers"
                ref={gigSeekersRef}
                label="Öppna för spelningar?"
                checked={bandData.band.gig_seekers}
              />
              {postError && (
                <p className="font-semibold text-error py-2">{postError}</p>
              )}
              <button
                className="btn btn-primary border border-base-content/20"
                type="submit"
                disabled={postLoading}
              >
                Spara
              </button>
            </form>
          </DropDownForm>
          {/* Images form. Profile and splash images */}
          <DropDownForm
            title="Bilder"
            about="Ladda upp profilbild och splashbild"
          >
            <form
              ref={formImages}
              onSubmit={handleSubmitImages}
              className="my-4"
            >
              <div className="flex space-x-8">
                <FormInputField
                  type="file"
                  label="Profilbild"
                  name="image_profile"
                  bottomLabel="Visningsstorlek 250x250px. Max 4mb"
                />
                {bandData.band.image_profile && (
                  <div className="h-28">
                    <p className="text-xs text-content">Nuvarande profilbild</p>
                    <img
                      className="object-contain w-full h-full"
                      src={bandData.band.image_profile}
                    />
                  </div>
                )}
              </div>
              <div className="flex space-x-8">
                <FormInputField
                  type="file"
                  label="Splashbild"
                  name="image_splash"
                  bottomLabel="Visningsstorlek 1280x562px. Max 4mb"
                />
                {bandData.band.image_splash && (
                  <div className="h-28">
                    <p className="text-xs text-content">Nuvarande Splashbild</p>
                    <img
                      className="object-contain w-full h-full"
                      src={bandData.band.image_splash}
                    />
                  </div>
                )}
              </div>
              {postImageError && (
                <p className="font-semibold text-error py-2">
                  {postImageError}
                </p>
              )}
              <button
                className="btn btn-primary border border-base-content/20"
                type="submit"
                disabled={postLoading}
              >
                Spara
              </button>
            </form>
          </DropDownForm>
          {/* Description form */}
          <DropDownForm
            title="Beskrivning"
            about="Beskriv er historia eller skriv något om vad som gör er musik speciell"
          >
            <form
              ref={fromDescription}
              onSubmit={handleSubmitDescription}
              className="my-4"
            >
              <RichTextEditorField
                label="Biografi"
                altLabel="Max 4000 tecken"
                value={bandDescription}
                onChange={setBandDescription}
              />
              {postError && (
                <p className="font-semibold text-error py-2">{postError}</p>
              )}
              <button
                className="btn btn-primary border border-base-content/20"
                type="submit"
                disabled={postLoading}
              >
                Spara
              </button>
            </form>
          </DropDownForm>
          {/* Social Media */}
          <DropDownForm
            title="Sociala medier"
            about="Länka till de platser ni redan finns på. Skriv antingen hela länken till er profil eller bara användarnamnet"
          >
            <form
              ref={formSocials}
              onSubmit={handleSubmitSocials}
              className="my-4"
            >
              <FormInputField
                type="text"
                name="facebook"
                label="Facebook"
                defaultValue={bandData.socials.facebook}
              />
              <FormInputField
                type="text"
                name="x"
                label="X"
                defaultValue={bandData.socials.x}
              />
              <FormInputField
                type="text"
                name="instagram"
                label="Instagram"
                defaultValue={bandData.socials.instagram}
              />
              <FormInputField
                type="text"
                name="youtube"
                label="Youtube"
                defaultValue={bandData.socials.youtube}
              />
              <FormInputField
                type="text"
                name="tiktok"
                label="Tiktok"
                defaultValue={bandData.socials.tiktok}
              />
              <FormInputField
                type="text"
                name="snapchat"
                label="Snapchat"
                defaultValue={bandData.socials.snapchat}
              />
              {postError && (
                <p className="font-semibold text-error py-2">{postError}</p>
              )}
              <button
                className="btn btn-primary border border-base-content/20"
                type="submit"
                disabled={postLoading}
              >
                Spara
              </button>
            </form>
          </DropDownForm>
          {/* Steaming platforms */}
          <DropDownForm
            title="Steamingplatformar"
            about="Vart kan man lyssna på er? Fyll ert unika id för platformen"
          >
            <form
              ref={formStreaming}
              onSubmit={handleSubmitStreaming}
              className="my-4"
            >
              <FormInputField
                type="text"
                name="spotify"
                label="Spotify"
                defaultValue={bandData.streamingplatforms.spotify}
              />
              <FormInputField
                type="text"
                name="apple_music"
                label="Apple music"
                defaultValue={bandData.streamingplatforms.apple_music}
              />
              <FormInputField
                type="text"
                name="soundcloud"
                label="Soundcloud"
                defaultValue={bandData.streamingplatforms.soundcloud}
              />
              <FormInputField
                type="text"
                name="youtube_music"
                label="Youtube music"
                defaultValue={bandData.streamingplatforms.youtube_music}
              />
              <FormInputField
                type="text"
                name="tidal"
                label="Tidal"
                defaultValue={bandData.streamingplatforms.tidal}
              />
              <FormInputField
                type="text"
                name="amazon_music"
                label="Amazon music"
                defaultValue={bandData.streamingplatforms.amazon_music}
              />
              <FormInputField
                type="text"
                name="bandcamp"
                label="Bandcamp"
                defaultValue={bandData.streamingplatforms.bandcamp}
              />
              {postError && (
                <p className="font-semibold text-error py-2">{postError}</p>
              )}
              <button
                className="btn btn-primary border border-base-content/20"
                type="submit"
                disabled={postLoading}
              >
                Spara
              </button>
            </form>
          </DropDownForm>
          {/* SubGenres / Alternative genres */}
          <DropDownForm
            title="Alternativa genres"
            about="Välj egna genres som beskriver er musik bäst"
          >
            <form
              ref={formSubGenres}
              onSubmit={handleSubmitSubGenres}
              className="my-4"
            >
              <AddSubGenres
                setAltGenres={setAltGenres}
                currentGenres={bandData.band.genre_sub}
              />
              {postError && (
                <p className="font-semibold text-error py-2">{postError}</p>
              )}
              <button
                className="btn btn-primary border border-base-content/20 my-4"
                type="submit"
                disabled={postLoading}
              >
                Spara
              </button>
            </form>
          </DropDownForm>
          {/* Spotify playlist Song */}
          <DropDownForm
            title="Spotify playlist"
            about="Vilka låtar vill ni ska läggas till i Lokults lokala spellista?"
          >
            <PopUpModulBtn
              btnText="Hur gör jag"
              modalId="howToAddSong"
              title="Hur hittar jag länken?"
              btnStyle="btn-neutral border border-base-content/20"
            >
              <p>Hitta låten i Spotify och i låtens alternativmeny: </p>
              <p className="font-semibold">Dela, Kopiera låtlänk</p> <br />
              <p>
                Så här kan en låt länk se ut{" "}
                <span className="italic">
                  https://open.spotify.com/track/75dSM9nk2SF9ENfMfpuYez?si=509e2f02f8ce49c7
                </span>
              </p>
            </PopUpModulBtn>

            <form
              ref={spotifyPlaylistRef}
              onSubmit={handleSubmitSpotifyPlaylist}
              className="my-4"
            >
              <FormInputField
                type="text"
                name="song1"
                label="Spotify låtlänk #1"
                placeholder="Låtlänk #1"
                bottomLabel={get_playlistsong_from_prio(1)}
              />
              <FormInputField
                type="text"
                name="song2"
                label="Spotify låtlänk #2"
                placeholder="Låtlänk #2"
                bottomLabel={get_playlistsong_from_prio(2)}
              />
              {postError && (
                <p className="font-semibold text-error py-2">{postError}</p>
              )}
              <button
                className="btn btn-primary border border-base-content/20"
                type="submit"
                disabled={postLoading}
              >
                Spara
              </button>
            </form>
          </DropDownForm>

          {/* Bottom of page delete band etc */}
          <div className="flex justify-between m-4">
            <a href={profilePageUrl}>
              <button className="btn btn-secondary border border-base-content/20">
                Visa Profil
              </button>
            </a>
            <PopUpModulBtn
              btnText="Radera"
              modalId="DeleteItem"
              title="Radera musikprojekt"
              btnStyle="btn-outline"
              text="Det är kommer att ta bort musikprojektet och all data anknuten till det. Åtgärden går inte att ångra!"
            >
              <div>
                <p className="my-2">
                  Skriv &quot;radera musikprojekt&quot; för att kunna radera.
                </p>
                <div className="flex">
                  <input
                    type="text"
                    placeholder="Är du säker?"
                    value={deleteProjectInput}
                    onChange={handleDeleteInputChange}
                    className="input input-bordered"
                  />
                  <button
                    className="btn btn-error border border-base-content/20 mx-4"
                    onClick={handleDeleteProject}
                    disabled={!deleteButtonIsActive}
                  >
                    Radera
                  </button>
                </div>
              </div>
            </PopUpModulBtn>
          </div>
        </div>
      </>
    );
  }
  if (!bandData) {
    return <p>loading</p>;
  }
};

export default CreateBandForm;
